// Econo-Pak case study — copy + data.
//
// DRAFT — pending Matt's approval before this goes live (external-content rule
// in CLAUDE.md). Everything a non-developer would edit lives here. Voice per
// references/voice.md: short sentences, warm, direct, no em dashes.
//
// Prose is Matt's voice (first person). Quotes are Sarah Richter's, lightly
// cleaned from the interview transcripts (docs/specs/econo-pak-transcripts.md).
// Consent to use her video + name was given. Fortune 500 brands stay generic,
// never named. The "1 to 16" figure is Matt's own result, never attributed to
// Sarah. Sourcing for every quote is in the spec (docs/specs/econo-pak-case-study.md).

import contextPoster from "@/assets/econo-pak-context-poster.jpg"
import approachPoster from "@/assets/econo-pak-testimonial-poster.jpg"
import momentPoster from "@/assets/econo-pak-moment-poster.jpg"
import proudPoster from "@/assets/econo-pak-proud-poster.jpg"
import lifenowPoster from "@/assets/econo-pak-lifenow-poster.jpg"

export const meta = {
  title: "Econo-Pak case study | Matthew Traynor",
  description:
    "How a dry-food co-packer that supplies Fortune 500 food brands went from one qualified quote a month to sixteen, sourcing million-dollar deals on Google and Bing.",
}

export const hero = {
  eyebrow: "Case study",
  title: "From one qualified quote a month to sixteen.",
  subhead:
    "A dry-food co-packer that supplies Fortune 500 food brands was spending real money on Google and Bing Ads and getting leads that didn't convert. Here's how we cleaned the traffic and turned paid search into a pipeline of million-dollar quotes.",
  ctaLabel: "Apply to work together",
  ctaHref: "#apply",
}

export interface VideoSection {
  /** Section id for in-page anchors (optional). */
  id?: string
  bg: "page" | "surface"
  eyebrow: string
  heading: string
  /** Narrative paragraphs, Matt's voice. */
  lead: string[]
  /** Video on the right (text left) on desktop. Alternated for rhythm. */
  reverse?: boolean
  video: {
    src: string
    poster: string
    subheader?: string
    impactTag?: string
    impactFacts?: string[]
    quote: string
    name?: string
    title?: string
    /** Default playback speed once the clip loads. */
    playbackRate?: number
  }
}

// Ordered narrative arc:
// Context -> What was different -> The moment -> Most proud of -> Life now.
export const videoSections: VideoSection[] = [
  {
    id: "context",
    bg: "surface",
    eyebrow: "Who they are",
    heading: "A million dollars a deal. One good lead changes the year.",
    lead: [
      "Econo-Pak is a B2B dry-food co-packer with over 1,000 staff. The sales cycle runs 6 to 12 months, so lead quality is everything.",
    ],
    video: {
      src: "/media/econo-pak-context.mp4",
      poster: contextPoster,
      quote:
        "Our main clients are Fortune 500 dry-food companies. We'd be making million-dollar-plus deals.",
      name: "Sarah Richter",
      title: "Sales and Marketing Manager, Econo-Pak",
      playbackRate: 1.25,
    },
  },
  {
    id: "approach",
    bg: "page",
    eyebrow: "What was different",
    heading: "We cleaned the traffic together, in the open.",
    reverse: true,
    lead: [
      "Before we worked together the spend was there but the quality wasn't. The conversion numbers looked fine on the surface, but the leads weren't the ones they wanted.",
      "So we fixed it in the open, together, with nothing hidden in a black box.",
    ],
    video: {
      src: "/media/econo-pak-testimonial.mp4",
      poster: approachPoster,
      impactTag: "The approach",
      impactFacts: [
        "Custom keyword system, built for them",
        "Bi-weekly working calls",
        "Google and Bing cleaned every fortnight",
      ],
      quote:
        "I've never seen any other PPC company do this before. You built a completely custom spreadsheet and walked me through it every call. Our leads are definitely way better quality.",
      playbackRate: 1.25,
    },
  },
  {
    id: "results",
    bg: "surface",
    eyebrow: "The moment it clicked",
    heading: "When paid traffic started closing.",
    lead: [
      "The proof wasn't a dashboard. It was Sarah's weekly review with her VP, the one scoreboard that actually counts.",
    ],
    video: {
      src: "/media/econo-pak-moment.mp4",
      poster: momentPoster,
      impactTag: "The result",
      impactFacts: [
        "Qualified quotes grew from 1 to 16",
        "All sourced from paid search",
      ],
      quote:
        "More and more leads were moving down the pipeline sourced from paid traffic. That's when I realized this is actually working.",
      playbackRate: 1.25,
    },
  },
  {
    id: "proud",
    bg: "page",
    eyebrow: "The standout",
    heading: "Deals they'd never have won.",
    reverse: true,
    lead: [
      "Paid search didn't just clean up the leads. It surfaced demand Econo-Pak never knew was there, buyers already looking who had simply never come across them.",
    ],
    video: {
      src: "/media/econo-pak-proud.mp4",
      poster: proudPoster,
      impactTag: "Most proud of",
      impactFacts: [
        "A Fortune 500 brand sourced via Bing",
        "In front of buyers who'd never heard of them",
        "Just 6 to 8 lean campaigns",
      ],
      quote:
        "We landed deals that, if not for paid search, these companies would have never heard of us. We had a Fortune 500 food brand come to us through Bing.",
      playbackRate: 1.25,
    },
  },
  {
    id: "life-now",
    bg: "surface",
    eyebrow: "Life now",
    heading: "Less firefighting. More closing.",
    lead: [
      "With lead quality handled, the account stopped being something Sarah had to firefight.",
    ],
    video: {
      src: "/media/econo-pak-lifenow.mp4",
      poster: lifenowPoster,
      quote:
        "I don't have to spend as much time thinking about our paid search campaigns. It freed up more time for me to focus on closing the deal.",
      playbackRate: 1.25,
    },
  },
]

// A quiet trust band before the form: reliability + how Matt works.
export const trust = {
  quote:
    "I knew I didn't have to worry about whether you'd get it done, because you always got it done, and you'd follow up to tell me how it went.",
  name: "Sarah Richter",
  title: "Sales and Marketing Manager, Econo-Pak",
  matt: "I run every account the way I ran this one. Hands-on, direct with me, and testing that never stops.",
}

export const closing = {
  eyebrow: "Apply",
  heading: "Want this for your account?",
  lead: "Tell me what you're spending and I'll come back with a straight answer.",
}
